<template>
  <MarketingImage
    :media="resolvedMedia"
    :fallback="resolvedFallback"
    :alt="alt"
    :image-class="imageClass"
    :image-style="imageStyle"
    :sizes="sizes"
    :width="width"
    :height="height"
    :loading="loading"
    :fetchpriority="fetchpriority"
  />
</template>

<script setup lang="ts">
import { computed, onMounted, onServerPrefetch, ref, type PropType } from "vue";
import {
  resolveMarketingAssetUrl,
  resolveMarketingMediaUrls,
  type MarketingMedia,
} from "../../../utils/marketingMedia";
import MarketingImage from "../marketing-image/marketing-image.vue";

const props = defineProps({
  slotCode: { type: String, required: true },
  fallback: { type: String, required: true },
  alt: { type: String, default: "" },
  imageClass: { type: [String, Array, Object] as PropType<any>, default: "" },
  imageStyle: { type: [String, Array, Object] as PropType<any>, default: "" },
  sizes: { type: String, default: "100vw" },
  width: { type: Number, default: 800 },
  height: { type: Number, default: 1000 },
  loading: { type: String as PropType<"eager" | "lazy">, default: "lazy" },
  fetchpriority: {
    type: String as PropType<"high" | "low" | "auto">,
    default: "auto",
  },
});

const media = ref<MarketingMedia | null>(null);
const MARKETING_CDN_ORIGIN = import.meta.env.VITE_CDN_URL || undefined;
const resolvedFallback = computed(() =>
  resolveMarketingAssetUrl(props.fallback, MARKETING_CDN_ORIGIN)
);
const resolvedMedia = computed(() =>
  resolveMarketingMediaUrls(
    media.value,
    resolvedFallback.value,
    MARKETING_CDN_ORIGIN
  )
);
const REST_API_URL = (import.meta.env.VITE_REST_API_URL || "").replace(
  /\/+$/,
  ""
);

async function loadSlot() {
  if (!REST_API_URL || media.value) return;
  try {
    const url = `${REST_API_URL}?route=api%2Fhooligans_media&slot=${encodeURIComponent(
      props.slotCode
    )}`;
    const response = await fetch(url);
    if (!response.ok) return;
    const payload = await response.json();
    if (payload?.media) media.value = payload.media;
  } catch (_error) {
    // The legacy image remains visible when the optional media endpoint is
    // unavailable during rollout or a slot has not been migrated yet.
  }
}

onServerPrefetch(loadSlot);
onMounted(loadSlot);
</script>
